export default function Work() {
  return (
    <main className="page">
      <header className="work__header">
        <h1>Selected Work</h1>
        <p>
          Three cases that show how I think — each one a different dimension
          of what it means to build and lead at the intersection of product, narrative, and scale.
        </p>
      </header>

      {/* ── CASE STUDY 1 ── */}
      <article className="case-study">
        <div className="case-study__meta">
          <span className="case-study__number">01</span>
          <h2 className="case-study__title">Making Enterprise AI Stick</h2>
          <div className="case-study__tags">
            <span className="tag">Product Strategy</span>
            <span className="tag">AI Adoption</span>
            <span className="tag">Enterprise GTM</span>
          </div>
        </div>

        <div className="case-study__content">
          <div className="case-study__section">
            <span className="case-study__section-label">The Problem</span>
            <p>
              Workday had embedded AI into its platform — but enterprise customers weren't
              activating it. This wasn't a technology problem. It was a trust problem, and
              it had real commercial consequences: SKU sales were stalling, renewals were
              exposed, and CSAT was at risk across the enterprise customer base.
            </p>
          </div>

          <div className="case-study__section">
            <span className="case-study__section-label">My Approach</span>
            <p>
              I identified early that "we do responsible AI" wasn't a sufficient answer
              for enterprise buyers. They needed to see AI working in their environment —
              their data, their workflows, their employees. So I partnered with senior
              solution consultants to build show-don't-tell demonstrations tailored to
              each customer's vertical and workforce reality.
            </p>
            <br />
            <p>
              On the product side, I made an opinionated call: give customers flexibility
              and control over their Hubs with dead-simple defaults, and embed AI targeted
              at professional users — validated by UX research proving these features solved
              real problems at work. I also built AI explainability features that made
              algorithmic decisions legible to end users, including an ML Rules Creator
              that turned a black-box system into something administrators could reason
              about and trust.
            </p>
            <br />
            <p>
              The insight that changed everything: enterprise buyers don't need to adopt
              everything at once. They need an opinionated sequenced roadmap — what to
              turn on first, in what order — to build employee AI fluency progressively.
              Giving buyers that sequence isn't hand-holding. It's the product doing its job.
            </p>
          </div>

          <div className="case-study__outcomes">
            <span className="case-study__section-label">Outcomes</span>
            <ul>
              <li>Grew Hubs with embedded AI from ~300K to 13MM monthly active users (2022–2026)</li>
              <li>Sustained 97% CSAT and 95%+ renewal rate across accounts generating $8B+ ARR</li>
              <li>Narrative strategy became a core driver of enterprise adoption at scale</li>
            </ul>
          </div>

          <div className="case-study__customers">
            <span className="case-study__section-label">In the Room</span>
            <p style={{ marginBottom: '1.25rem' }}>
              Account teams requested my involvement directly — to close a new SKU sale,
              accelerate a renewal, or protect customer satisfaction. Three that stand out:
            </p>
            <div className="customer-list">
              <div className="customer-item">
                <span className="customer-name">Nvidia</span>
                <p className="customer-detail">
                  They wanted to understand how we were inventing AI — not just applying it.
                  A peer-level conversation about methodology. It went well enough that the
                  account team asked me to meet them in person at Rising for a follow-up.
                </p>
              </div>
              <div className="customer-item">
                <span className="customer-name">NY Presbyterian</span>
                <p className="customer-detail">
                  High-stakes regulated environment. The CHRO was evaluating AI adoption
                  for a workforce of thousands of healthcare professionals. The presentation
                  went well enough that he asked me to present at his internal leadership offsite.
                </p>
              </div>
              <div className="customer-item">
                <span className="customer-name">Midwest manufacturer</span>
                <p className="customer-detail">
                  A cement company with ~2,000 employees asking a fundamental question:
                  what does AI transformation actually mean for a company like ours?
                  Built the answer with them from the ground up.
                </p>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* ── CASE STUDY 2 — PLACEHOLDER ── */}
      <article className="case-study case-study--placeholder">
        <div className="case-study__meta">
          <span className="case-study__number">02</span>
          <h2 className="case-study__title">Building the Narrative Machine</h2>
          <div className="case-study__tags">
            <span className="tag">GTM Strategy</span>
            <span className="tag">Field Enablement</span>
            <span className="tag">Org Design</span>
          </div>
          <span className="coming-soon">Coming soon</span>
        </div>

        <div className="case-study__content">
          <div className="case-study__section">
            <span className="case-study__section-label">The Problem</span>
            <p>
              Workday had a platform story — but no unified way to tell it. Sales,
              solution consulting, marketing, and support were each telling a different
              version. At scale, that fragmentation costs deals.
            </p>
          </div>
          <div className="case-study__section">
            <span className="case-study__section-label">Coming soon</span>
            <p>Full case study in progress.</p>
          </div>
        </div>
      </article>

      {/* ── CASE STUDY 3 — PLACEHOLDER ── */}
      <article className="case-study case-study--placeholder">
        <div className="case-study__meta">
          <span className="case-study__number">03</span>
          <h2 className="case-study__title">Building the Org</h2>
          <div className="case-study__tags">
            <span className="tag">Leadership</span>
            <span className="tag">Design Org</span>
            <span className="tag">Culture</span>
          </div>
          <span className="coming-soon">Coming soon</span>
        </div>

        <div className="case-study__content">
          <div className="case-study__section">
            <span className="case-study__section-label">The Problem</span>
            <p>
              Workday needed design, research, and content to operate as a unified
              strategic function — not a service org that executes on PM decisions
              after the fact.
            </p>
          </div>
          <div className="case-study__section">
            <span className="case-study__section-label">Coming soon</span>
            <p>Full case study in progress.</p>
          </div>
        </div>
      </article>
    </main>
  );
}
